import type { PortalAudienceRule } from '../../types/portalLibrary.js';
import type { InspirationAdaptFor, InspirationUsage, PortalInspirationEntry } from '../../types/portalInspiration.js';

// PORTAL INSPIRATION — the staff edit form ⇄ the PATCH body. PURE.

export type InspirationEditForm = {
  title: string;
  trendLine: string;
  /** One replacement line per row. */
  suggestedLines: string;
  adaptFor: InspirationAdaptFor;
  description: string;
  howTo: string;
  usage: InspirationUsage | '';
  tags: string[];
  audience: PortalAudienceRule[];
  /** `yyyy-mm-dd` (local calendar), '' = from now. */
  visibleFromDate: string;
  /** `yyyy-mm-dd`, '' = never comes down. */
  visibleUntilDate: string;
};

const pad = (n: number) => String(n).padStart(2, '0');

/** ISO instant → the local `yyyy-mm-dd` the date picker shows. */
export function isoToDateInput(iso: string | null): string {
  if (iso === null) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parts(date: string): [number, number, number] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

/** Start of that LOCAL day. */
export function dateToFromMs(date: string): number | null {
  const p = parts(date);
  return p ? new Date(p[0], p[1] - 1, p[2], 0, 0, 0, 0).getTime() : null;
}

/** ⚠️ The END of that local day, so "until 30 Oct" survives the whole of 30 Oct (the hosted-report rule). */
export function dateToUntilMs(date: string): number | null {
  const p = parts(date);
  return p ? new Date(p[0], p[1] - 1, p[2], 23, 59, 59, 999).getTime() : null;
}

/** One replacement line per row; blanks dropped. */
export function splitLines(text: string): string[] {
  return text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l !== '');
}

export function formFromEntry(e: PortalInspirationEntry): InspirationEditForm {
  return {
    title: e.title,
    trendLine: e.trendLine ?? '',
    suggestedLines: e.suggestedLines.join('\n'),
    adaptFor: e.adaptFor,
    description: e.description,
    howTo: e.howTo,
    usage: e.usage ?? '',
    tags: [...e.tags],
    audience: [...e.audience],
    visibleFromDate: isoToDateInput(e.visibleFrom),
    visibleUntilDate: isoToDateInput(e.visibleUntil),
  };
}

export type InspirationPatch = Partial<{
  title: string;
  trendLine: string | null;
  suggestedLines: string[];
  adaptFor: InspirationAdaptFor;
  description: string;
  howTo: string;
  usage: InspirationUsage | null;
  tags: string[];
  audience: PortalAudienceRule[];
  visibleFromMs: number;
  visibleUntilMs: number | null;
}>;

/**
 * ⚠️ Only what CHANGED is sent. That matters for provenance: the server decides "a person edited this"
 * by comparing stored and new values, and a form that re-sent every field would still be safe there,
 * but sending nothing for an untouched field keeps the audit entry honest about what was touched.
 */
export function buildPatch(initial: InspirationEditForm, now: InspirationEditForm): InspirationPatch {
  const out: InspirationPatch = {};
  if (now.title !== initial.title) out.title = now.title;
  if (now.trendLine !== initial.trendLine) out.trendLine = now.trendLine.trim() === '' ? null : now.trendLine;
  if (now.suggestedLines !== initial.suggestedLines) out.suggestedLines = splitLines(now.suggestedLines);
  if (now.adaptFor !== initial.adaptFor) out.adaptFor = now.adaptFor;
  if (now.description !== initial.description) out.description = now.description;
  if (now.howTo !== initial.howTo) out.howTo = now.howTo;
  if (now.usage !== initial.usage) out.usage = now.usage === '' ? null : now.usage;
  if (JSON.stringify(now.tags) !== JSON.stringify(initial.tags)) out.tags = now.tags;
  if (JSON.stringify(now.audience) !== JSON.stringify(initial.audience)) out.audience = now.audience;
  if (now.visibleFromDate !== initial.visibleFromDate) {
    const ms = now.visibleFromDate === '' ? Date.now() : dateToFromMs(now.visibleFromDate);
    if (ms !== null) out.visibleFromMs = ms;
  }
  if (now.visibleUntilDate !== initial.visibleUntilDate) {
    out.visibleUntilMs = now.visibleUntilDate === '' ? null : dateToUntilMs(now.visibleUntilDate);
  }
  return out;
}

export function isDirty(initial: InspirationEditForm, now: InspirationEditForm): boolean {
  return Object.keys(buildPatch(initial, now)).length > 0;
}
